import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import api from '@/services/api';

const schema = z.object({
  email: z.string().email('Email inválido'),
});

type Form = z.infer<typeof schema>;

export default function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<Form>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: Form) => {
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: data.email });
    } catch {
      // Por seguridad mostramos el mismo mensaje aunque falle
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-cream-50 px-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm text-taupe-600 hover:text-taupe-800 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver al login
        </Link>

        <div className="bg-white rounded-2xl shadow-sm border border-cream-200 p-8">
          {sent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-taupe-800 mb-2">Revisá tu email</h1>
              <p className="text-taupe-600 text-sm mb-6">
                Si el email está registrado, te enviamos una <strong>contraseña temporal</strong> válida por <strong>1 hora</strong>.
              </p>
              <p className="text-taupe-500 text-xs">
                No olvides revisar la carpeta de spam.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-sm text-taupe-700 hover:text-taupe-900 font-medium"
              >
                Volver al login
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-taupe-800 mb-2">¿Olvidaste tu contraseña?</h1>
              <p className="text-taupe-600 text-sm mb-6">
                Ingresá tu email y te enviamos una contraseña temporal, válida por 1 hora.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <Input
                  id="email"
                  type="email"
                  label="Email"
                  placeholder="tu@email.com"
                  autoComplete="email"
                  {...register('email')}
                  error={errors.email?.message}
                />

                <Button
                  type="submit"
                  className="w-full bg-taupe-700 hover:bg-taupe-800 text-cream-50 font-medium py-3 rounded-xl"
                  loading={loading}
                >
                  Enviar contraseña temporal
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
